<template>
  <div class="theme-images h-full flex flex-col">
    <NGrid class="h-full overflow-hidden" cols="12" item-responsive>
      <NGridItem class="h-full overflow-auto" :span="'12 650:7 1200:8 1400:9'">
        <div class="mr-3 h-full">
          <!-- Show message when no theme is active -->
          <div v-if="!activeTheme" class="text-center py-10">
            <CIcon class="mb-4 color-primary3" icon="image" size="48" />
            <p>{{ t('canvasUI.contentSelector.themeImages.noActiveTheme') }}</p>
          </div>

          <!-- Logo section -->
          <div v-else-if="activeTheme" class="mb-8">
            <h3 class="mb-3">{{ t('canvasUI.contentSelector.themeImages.logo') }}</h3>
            <NSpace v-if="activeTheme.logo" wrap>
              <div
                class="rounded-2 pa-3 flex flex-col gap-1 cursor-pointer relative"
                :class="isAssetSelected(activeTheme.logo.id) ? 'bg-primary4' : 'bg-base'"
                @click="onSelectThemeAsset(activeTheme.logo)"
              >
                <CImage
                  class="rounded-2"
                  height="135"
                  :img-props="{ draggable: false, class: 'max-w-full max-h-full' }"
                  lazy
                  object-fit="contain"
                  preview-disabled
                  :src="activeTheme.logo.url"
                  width="264"
                />
                <div class="text-truncate max-w-[264px] font-bold text-center">
                  {{ activeTheme.logo.name }}
                </div>
                <div class="flex items-center justify-center w-full">
                  <div class="text-truncate text-center" :style="{ color: themeVars.text3 }">
                    {{ getFileExtension(activeTheme.logo.url) }}
                  </div>
                </div>
              </div>
            </NSpace>
            <div v-else class="text-center py-5 border rounded-md bg-primary6">
              <CIcon class="mb-2 color-primary3" icon="image" size="32" />
              <p>{{ t('canvasUI.contentSelector.themeImages.noLogo') }}</p>
            </div>
          </div>

          <!-- Theme assets grid -->
          <div v-if="activeTheme && themeAssets.length > 0" class="mb-4">
            <h3 class="mb-3">{{ t('canvasUI.contentSelector.themeImages.assets') }}</h3>
            <NSpace wrap>
              <div
                v-for="asset in themeAssets"
                :key="asset.id"
                class="rounded-2 pa-3 flex flex-col gap-1 cursor-pointer relative"
                :class="isAssetSelected(asset.id) ? 'bg-primary4' : 'bg-base'"
                @click="onSelectThemeAsset(asset)"
              >
                <CImage
                  class="rounded-2"
                  height="135"
                  :img-props="{ draggable: false, class: 'max-w-full max-h-full' }"
                  lazy
                  object-fit="contain"
                  preview-disabled
                  :src="asset.url"
                  width="264"
                />
                <div class="text-truncate max-w-[264px] font-bold text-center">
                  {{ asset.name }}
                </div>
                <div class="flex items-center justify-center w-full">
                  <div class="text-truncate text-center" :style="{ color: themeVars.text3 }">
                    {{ getFileExtension(asset.url) }}
                  </div>
                </div>
              </div>
            </NSpace>
          </div>
          <div
            v-else-if="activeTheme && themeAssets.length === 0"
            class="text-center py-5 border rounded-md bg-primary6"
          >
            <CIcon class="mb-2 color-primary3" icon="images" size="32" />
            <p>{{ t('canvasUI.contentSelector.themeImages.noAssets') }}</p>
          </div>
        </div>
      </NGridItem>
      <NGridItem class="h-full overflow-auto" :span="'12 650:5 1200:4 1400:3'">
        <SelectionPanel />
      </NGridItem>
    </NGrid>
  </div>
</template>

<script setup lang="ts">
import { NGridItem, NGrid, NSpace } from 'naive-ui'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CImage from '@lib/components/CImage/CImage.vue'
import SelectionPanel from '@content-selector/components/Content/SelectionPanel/SelectionPanel.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import { useStore } from '@content-selector/composables/useStore'
import type { CanvasThemeAsset } from '@lib/types/canvasTheme'

const { t } = useI18n()
const themeVars = useThemeVars()
const { activeTheme } = useCanvasTheme()
const { selectItem, deselectItem, selectedItems } = useStore()

const themeAssets = computed(() => activeTheme.value?.assets || [])

function onSelectThemeAsset(asset: CanvasThemeAsset) {
  const isSelected = isAssetSelected(asset.id)

  if (isSelected) {
    // Find and deselect the item
    const selectedItem = selectedItems.value.find((item) => item.id === asset.id)
    if (selectedItem) {
      deselectItem(selectedItem)
    }
  } else {
    const isLogo = activeTheme.value?.logo?.id === asset.id

    selectItem({
      id: asset.id,
      file: {
        id: asset.id,
        name: asset.name || 'Theme Asset',
      },
      content_url: asset.url,
      thumbnail_url: asset.url,
      type: 'asset',
      content_extension: getFileExtension(asset.url).toLowerCase(),
      theme_meta: isLogo
        ? {
            // For logo: set is_theme_logo to true and remove theme_asset_index
            is_theme_logo: true,
            theme_asset_index: undefined,
          }
        : {
            // For regular theme asset: set theme_asset_index and ensure is_theme_logo is false
            is_theme_logo: false,
            theme_asset_index: activeTheme.value?.assets?.findIndex((a) => a.id === asset.id),
          },
    })
  }
}

function isAssetSelected(assetId: string): boolean {
  return selectedItems.value.some((item) => item.id === assetId)
}

function getFileExtension(url: string): string {
  const urlWithoutParams = url.split('?')[0]
  const extension = urlWithoutParams.split('.').pop()?.toLowerCase() || ''
  return extension.length > 0
    ? extension.toUpperCase()
    : t('canvasUI.canvasBuilder.multimediaSettings.image').toUpperCase()
}
</script>
